import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { History } from 'lucide-react'
import { useSearchParams } from 'wouter'
import {
  Alert,
  Button,
  Chip,
  Drawer,
  EmptyState,
  Skeleton,
  TablePagination,
  humanize,
  FormattedDate,
} from '@ideon-api/ideon-ui'
import { entryDelta, entryTitle } from '../../lib/audit-delta'
import { AREAS, areaParam, areasFor, parseHistoryParams, resourceTypesFor, sinceFor, type ResourceType } from '../../lib/history-filters'
import { useChangeSets, type ChangeSetRow } from '../../lib/use-change-sets'
import { ChangeSetEntries, DeltaLine } from './ChangeSetEntries'
import { HistoryActor } from './HistoryActor'
import { HistoryFilters } from './HistoryFilters'

type Props = { slug: string; permissionSets: ReadonlyArray<{ key: string }> }

// The five fixed columns need about 690px, so the grid starts at lg; below it each row stacks with labels.
const GRID = 'grid gap-x-4 gap-y-1 lg:grid-cols-[150px_170px_160px_140px_minmax(0,1fr)] lg:items-center'
const MAPPED_TYPES = new Set<string>(AREAS.flatMap((a) => a.resourceTypes))
const AREA_LABELS = new Map(AREAS.map((a) => [a.value, a.label]))

const countLabel = (n: number) => `${n} ${n === 1 ? 'change' : 'changes'}`

function areaLabels(resourceTypes: readonly string[]): string[] {
  const unmapped = resourceTypes.filter((t) => !MAPPED_TYPES.has(t)).map((t) => humanize(t))
  return [...areasFor(resourceTypes).map((value) => AREA_LABELS.get(value) ?? value), ...unmapped]
}

function AreaChips({ resourceTypes }: { resourceTypes: readonly string[] }) {
  const labels = areaLabels(resourceTypes)
  if (labels.length === 0) return <>—</>
  return (
    <div className="flex flex-wrap gap-1">
      {labels.map((label) => (
        <Chip key={label} variant="outlined">
          {label}
        </Chip>
      ))}
    </div>
  )
}

// Stacked rows (below lg) carry their own column label; the header row replaces it at lg.
function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <span className="w-24 shrink-0 text-xs text-ideon-gray-70 lg:hidden">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

function HistoryHeaderRow() {
  return (
    <div className={`${GRID} bg-ideon-gray-10 px-4 py-2 text-[13px] font-semibold max-lg:hidden`}>
      <span>When</span>
      <span>Who</span>
      <span>Permission set</span>
      <span>Area</span>
      <span>Change</span>
    </div>
  )
}

// Same frame as the loaded table, so rows fill in without the page moving.
export function HistoryTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-ideon-gray-40 bg-white">
      <HistoryHeaderRow />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="border-t border-ideon-gray-40 px-4 py-3">
          <Skeleton className="h-5 w-full" />
        </div>
      ))}
    </div>
  )
}

// Disabled filter bar plus table frame, so the tab does not shift when data lands.
export function HistorySkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div inert className="opacity-50">
        <HistoryFilters permissionSets={[]} value={parseHistoryParams(new URLSearchParams())} onChange={() => {}} />
      </div>
      <HistoryTableSkeleton />
    </div>
  )
}

// The full list is in the drawer; a whole-row create would otherwise show a badge per state in the button.
const SUMMARY_DELTAS = 2

function Summary({ entry }: { entry: NonNullable<ChangeSetRow['summaryEntry']> }) {
  const diff = entry.diff ?? {}
  const deltas = entryDelta(diff, entry.resourceType ?? '')
  return (
    <span className="flex flex-wrap items-center gap-1 text-sm font-normal text-ideon-gray-70">
      <span>{entryTitle({ resourceType: entry.resourceType ?? '', diff })}</span>
      {deltas.slice(0, SUMMARY_DELTAS).map((d) => (
        <DeltaLine key={d.field} delta={d} oneSided={entry.action !== 'update'} />
      ))}
      {deltas.length > SUMMARY_DELTAS && <span>{`+${deltas.length - SUMMARY_DELTAS} more`}</span>}
    </span>
  )
}

function ChangeSetRowView({ row, onOpen }: { row: ChangeSetRow; onOpen: () => void }) {
  return (
    <div className={`${GRID} border-t border-ideon-gray-40 px-4 py-3 text-sm first:border-t-0`}>
      <Labelled label="When">
        <FormattedDate value={row.createdAt ?? ''} showTime />
      </Labelled>
      <Labelled label="Who">
        <HistoryActor actorType={row.actorType ?? ''} actorId={row.actorId ?? null} actorName={row.actorName ?? null} />
      </Labelled>
      <Labelled label="Permission set">
        <span className="break-all">{row.permissionSetKey ?? '—'}</span>
      </Labelled>
      <Labelled label="Area">
        <AreaChips resourceTypes={row.resourceTypes ?? []} />
      </Labelled>
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-col items-start gap-0.5 rounded text-left hover:bg-ideon-gray-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ideon-magenta-70"
      >
        <span className="font-semibold text-ideon-magenta-70">{countLabel(row.changeCount ?? 0)}</span>
        {row.summaryEntry && <Summary entry={row.summaryEntry} />}
      </button>
    </div>
  )
}

function ChangeSetDrawer({
  row,
  isOpen,
  onClose,
  slug,
  resourceType,
  since,
}: {
  row: ChangeSetRow
  isOpen: boolean
  onClose: () => void
  slug: string
  resourceType: ResourceType[]
  since: string | undefined
}) {
  return (
    <Drawer.Root isOpen={isOpen} onClose={onClose} size="lg" testIdPrefix="change-set-drawer">
      <Drawer.Header className="mb-4 border-b border-ideon-gray-40 pb-4">
        <div className="flex min-w-0 flex-col gap-1">
          <Drawer.Title className="text-lg leading-7">{countLabel(row.changeCount ?? 0)}</Drawer.Title>
          <span className="flex flex-wrap items-center gap-x-2 text-sm text-ideon-gray-70">
            <FormattedDate value={row.createdAt ?? ''} showTime />
            <span>·</span>
            <HistoryActor actorType={row.actorType ?? ''} actorId={row.actorId ?? null} actorName={row.actorName ?? null} />
            {row.permissionSetKey && (
              <>
                <span>·</span>
                <span>{row.permissionSetKey}</span>
              </>
            )}
          </span>
        </div>
        <Drawer.CloseButton />
      </Drawer.Header>
      <Drawer.Body>
        <ChangeSetEntries
          slug={slug}
          changeSetId={row.changeSetId ?? ''}
          permissionSetKey={row.permissionSetKey ?? null}
          resourceType={resourceType}
          since={since}
        />
      </Drawer.Body>
    </Drawer.Root>
  )
}

export function HistoryTab({ slug, permissionSets }: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const params = parseHistoryParams(searchParams)
  const resourceType = resourceTypesFor(params.areas)
  // Memoised on the period: a fresh clock read per render would change the request every render.
  const since = useMemo(() => sinceFor(params.period, new Date()), [params.period])

  // The row stays set after close so the drawer can run its close transition.
  const [opened, setOpened] = useState<{ row: ChangeSetRow; filterKey: string } | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  // The drawer renders only while the filters it opened under still match, so a filter change never fetches for a stale drawer.
  const filterKey = `${params.areas.join(',')}|${params.period ?? ''}`
  const openRow = opened?.filterKey === filterKey ? opened.row : null

  const state = useChangeSets(slug, {
    page: params.page,
    perPage: params.perPage,
    resourceType,
    permissionSetKey: params.set,
    since,
  })

  const write = (change: Record<string, string | number | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(change)) {
          if (value === undefined) next.delete(key)
          else next.set(key, String(value))
        }
        return next
      },
      { replace: true }
    )

  // A stale bookmark can point past the last page; fall back to page 1 instead of a dead end.
  const outOfRange = state.status === 'ready' && state.data.length === 0 && state.meta.totalCount > 0 && params.page > 1
  useEffect(() => {
    if (outOfRange) write({ page: undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- write only touches setSearchParams
  }, [outOfRange])

  const unknownSet = params.set !== undefined && !permissionSets.some((s) => s.key === params.set)
  const narrowed = params.areas.length > 0 || params.set !== undefined || params.period !== undefined

  return (
    <div className="flex flex-col gap-6">
      <HistoryFilters
        permissionSets={permissionSets}
        value={params}
        onChange={write}
      />

      {unknownSet && (
        <Alert variant="warning">
          {`Unknown permission set '${params.set}'.`}
        </Alert>
      )}

      {state.status === 'loading' && <HistoryTableSkeleton />}

      {state.status === 'error' && <Alert variant="error">{state.message}</Alert>}

      {state.status === 'ready' &&
        (state.data.length === 0 ? (
          <EmptyState
            className="bg-white"
            icon={<History className="h-10 w-10" />}
            heading={narrowed ? 'No changes match these filters' : 'No changes'}
            description="Changes to this account's entitlements and settings appear here."
            action={
              narrowed ? (
                <Button variant="text" onClick={() => write({ area: areaParam([]), set: undefined, period: undefined, page: undefined })}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className={`flex flex-col gap-4${state.pending ? ' opacity-60' : ''}`}>
            <div className="overflow-hidden rounded-lg border border-ideon-gray-40 bg-white">
              <HistoryHeaderRow />
              {state.data.map((row) => (
                <ChangeSetRowView
                  key={`${row.changeSetId}:${row.permissionSetKey ?? ''}`}
                  row={row}
                  onOpen={() => {
                    setOpened({ row, filterKey })
                    setDrawerOpen(true)
                  }}
                />
              ))}
            </div>
            <TablePagination
              currentPage={state.meta.page}
              pageSize={state.meta.perPage}
              totalCount={state.meta.totalCount}
              totalPages={state.meta.totalPages}
              onPageChange={(page) => write({ page })}
              onPageSizeChange={(size) => write({ per_page: size, page: 1 })}
            />
          </div>
        ))}

      {openRow && (
        <ChangeSetDrawer
          key={`${openRow.changeSetId}:${openRow.permissionSetKey ?? ''}`}
          row={openRow}
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          slug={slug}
          resourceType={resourceType}
          since={since}
        />
      )}
    </div>
  )
}
